// src/components/SubscriptionPrompt.js
import React, { useState, useEffect, useRef } from "react";
import { BiRightArrowAlt } from "react-icons/bi";
import { collection, addDoc, serverTimestamp } from "firebase/firestore/lite";
import { db } from "../Firebase";
import styles from "./SubscriptionPrompt.module.scss";
import { useCookieConsent } from "../context/CookieConsentContext"; // Import hook

function SubscriptionPrompt() {
  const { cookieConsent, updateConsent } = useCookieConsent(); // Use global state

  const [showPrompt, setShowPrompt] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  // Animation states
  const [animateSubscriptionOut, setAnimateSubscriptionOut] = useState(false);
  const [animateSubscriptionRight, setAnimateSubscriptionRight] =
    useState(false);
  const [animateCookieConsentOut, setAnimateCookieConsentOut] = useState(false);

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const scrollInitiated = useRef(false);

  useEffect(() => {
    const hasSubscribed = localStorage.getItem("hasSubscribed");

    // Logic: If already subscribed AND cookies decided, don't trigger scroll listener
    if (hasSubscribed && cookieConsent !== null) return;

    const handleScroll = () => {
      if (!scrollInitiated.current && window.scrollY > 0) {
        scrollInitiated.current = true;
        setTimeout(() => {
          // Only show subscription if they haven't subscribed
          if (!hasSubscribed) setShowPrompt(true);

          // Only show cookie prompt if they haven't decided yet
          if (cookieConsent === null) setShowCookieConsent(true);
        }, 5000);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [cookieConsent]);

  const handleClose = () => {
    setAnimateSubscriptionOut(true);
    setTimeout(() => setShowPrompt(false), 500);
  };

  const handleAcceptCookies = () => {
    setAnimateCookieConsentOut(true);
    setTimeout(() => {
      updateConsent(true); // Save to Context/LocalStorage
    }, 500);
  };

  const handleDeclineCookies = () => {
    setAnimateCookieConsentOut(true);
    setTimeout(() => {
      updateConsent(false); // Save to Context/LocalStorage
    }, 500);
  };

  const handleEmail = async () => {
    const trimmedEmail = email.trim();
    if (
      !trimmedEmail ||
      !trimmedEmail.includes("@") ||
      !trimmedEmail.includes(".")
    ) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      await addDoc(collection(db, "subscribers"), {
        email: trimmedEmail.toLowerCase(),
        subscribedAt: serverTimestamp(),
        source: "subscription-prompt",
      });

      setSubmitStatus("success");
      localStorage.setItem("hasSubscribed", "true");

      setTimeout(() => {
        setAnimateSubscriptionRight(true);
        setTimeout(() => setShowPrompt(false), 500);
      }, 800);
    } catch (error) {
      console.error("Error saving subscriber email:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.subscriptionPrompt}>
      {showPrompt && (
        <div
          className={`${animateSubscriptionOut ? styles.fadeOutDown : ""} ${animateSubscriptionRight ? styles.fadeOutRight : ""}`}
        >
          <button className={styles.closeBtn} onClick={handleClose}>
            ×
          </button>
          <div
            className={`${styles.subscriptionContent} ${animateSubscriptionOut ? styles.fadeOutDown : ""} ${animateSubscriptionRight ? styles.fadeOutRight : ""}`}
          >
            <p>
              Be the first to know <br /> Cork Matter's next steps
            </p>
            <div className={styles.emailInput}>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleEmail()}
                type="email"
                placeholder="Email Address"
                disabled={isSubmitting}
              />
              <button
                className={styles.emailBtn}
                onClick={handleEmail}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className={styles.spinner}>↻</span>
                ) : (
                  <BiRightArrowAlt />
                )}
              </button>
            </div>
            {submitStatus === "success" && (
              <p className={styles.successMessage}>
                Thank you! We'll be in touch.
              </p>
            )}
            {submitStatus === "error" && (
              <p className={styles.errorMessage}>
                Invalid email. Please try again.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Spacer Line: Show only if prompt is visible AND cookies not yet decided */}
      {showPrompt && showCookieConsent && cookieConsent === null && (
        <div className={styles.lineSpacer}></div>
      )}

      {/* Cookie Consent: Show only if initialized via scroll AND no decision made yet */}
      {showCookieConsent && cookieConsent === null && (
        <div
          className={`${styles.cookieConsent} ${animateCookieConsentOut ? styles.fadeOutDown : ""}`}
        >
          <div className={styles.cookiesTagline}>
            We use cookies to make things better
          </div>
          <div className={styles.cookiesButtons}>
            <button className={styles.accept} onClick={handleAcceptCookies}>
              ● Accept
            </button>
            <button className={styles.decline} onClick={handleDeclineCookies}>
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPrompt;
